// BUILD-042 — My HR overview (Owner decision 2026-10-24, "Admin versus employee view").
// "My HR uses compact Needs your action as the primary work list, not giant redundant zero cards; personal employment / benefits / property
//  information is secondary."
//
// Order: (1) a compact "Needs your action" card with the employee's REAL items (document signatures / uploads, corrections, tasks) and one amber count
// only when something is waiting; (2) the employee's own current employment; (3) two small secondary links (Benefits, Equipment & property).
// Nothing here is a placeholder: there is no sample qualification or "on file" claim, and a failed check is shown as "could not check", never as "nothing".
// There are no administrative controls on this page - HR setup lives in HR Administration.
import { CardTitle } from './SectionHeading';
import { MyAttentionList } from './AttentionList';
import type { AttentionSlice, MyAttention } from './attentionModel';
import type { SectionKey } from './sectionGroups';
import type { OverviewInfo } from './OverviewSection';
import type { PropertyLoad } from './usePropertySchedule';
import { formatLocalDate } from '../hrFormat';

function statusBadge(status: string | null) {
  if (!status) return <span className="text-sm font-semibold text-gray-900">—</span>;
  const isActive = status.toLowerCase() === 'active';
  const label = status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  return (
    <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
      {isActive && <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5" aria-hidden="true" />}
      {label}
    </span>
  );
}

/** The Property row is REAL: the same schedule (and counts) as Equipment & Property. */
function propertyLine(property: PropertyLoad | undefined): string {
  if (!property || property.status === 'loading') return 'Checking…';
  if (property.status === 'error') return 'Could not check';
  const n = property.schedule.counts.onRecord;
  return n === 0 ? 'None issued' : `${n} ${n === 1 ? 'item' : 'items'} on record`;
}

export function MyHrOverview({ info, onNavigate, property, attention }: {
  info: OverviewInfo; onNavigate: (key: SectionKey) => void; property?: PropertyLoad; attention: AttentionSlice<MyAttention>;
}) {
  const waiting = attention.status !== 'error' && attention.data ? attention.data.total : 0;
  return (
    <div className="space-y-4" data-testid="my-hr-overview">
      <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5" aria-label="Needs your action" data-testid="overview-needs-action-card">
        <CardTitle action={waiting > 0 ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-amber-900 bg-amber-100 border border-amber-300" data-testid="needs-action-count">{waiting}</span> : undefined}>
          <span className="text-base font-bold text-gray-900">Needs your action</span>
        </CardTitle>
        <MyAttentionList slice={attention} />
      </section>

      <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5" aria-label="Your employment">
        <div className="border-b border-gray-200 pb-3 mb-3">
          <CardTitle action={<button type="button" onClick={() => onNavigate('employment')} className="text-xs text-[#176A9B] hover:underline">View employment →</button>}>
            <span className="text-base font-bold text-gray-900">Your employment</span>
          </CardTitle>
        </div>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
          <div className="min-w-0"><dt className="text-xs text-gray-500">Status</dt><dd className="mt-0.5">{statusBadge(info.employmentStatus)}</dd></div>
          <div className="min-w-0"><dt className="text-xs text-gray-500">Title</dt><dd className="text-sm font-semibold text-gray-900 mt-0.5 break-words">{info.title ?? '—'}</dd></div>
          <div className="min-w-0"><dt className="text-xs text-gray-500">Manager</dt><dd className="text-sm font-semibold text-gray-900 mt-0.5 break-words">{info.managerName ?? '—'}</dd></div>
          <div className="min-w-0"><dt className="text-xs text-gray-500">Hire date</dt><dd className="text-sm font-semibold text-gray-900 mt-0.5">{formatLocalDate(info.hireDate)}</dd></div>
          <div className="min-w-0"><dt className="text-xs text-gray-500">Classification</dt><dd className="text-sm font-semibold text-gray-900 mt-0.5 break-words">{info.classification ?? '—'}</dd></div>
          <div className="min-w-0"><dt className="text-xs text-gray-500">Last update</dt><dd className="text-sm font-semibold text-gray-900 mt-0.5 break-words">{info.latestChangeReason ?? '—'}</dd></div>
        </dl>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button type="button" onClick={() => onNavigate('benefits')} className="text-left bg-white rounded-lg border border-gray-200 shadow-sm p-4 hover:border-[#4AABDE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]" data-testid="overview-benefits-link">
          <div className="text-sm font-semibold text-gray-900">Benefits</div>
          <div className="text-xs text-gray-500 mt-0.5">Plan information and your eligibility</div>
          <div className="text-xs text-[#176A9B] mt-2">View benefits →</div>
        </button>
        <button type="button" onClick={() => onNavigate('property')} className="text-left bg-white rounded-lg border border-gray-200 shadow-sm p-4 hover:border-[#4AABDE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]" data-testid="overview-property-tile">
          <div className="text-sm font-semibold text-gray-900">Equipment &amp; property</div>
          <div className="text-xs text-gray-500 mt-0.5" data-testid="overview-property-tile-value">{propertyLine(property)}</div>
          <div className="text-xs text-[#176A9B] mt-2">View equipment →</div>
        </button>
      </div>
    </div>
  );
}
